import React, { createContext, useState, useEffect, useCallback } from 'react';
import CONSTANTS from '../../assets/constants';
import PropTypes from 'prop-types';

export const defaultAnswers = {
  [CONSTANTS.REDUX.RISK]: { value: '', answered: false },
  [CONSTANTS.REDUX.TREATMENT_PLAN]: { value: '', answered: false },
  [CONSTANTS.REDUX.TREATMENT_TIMING]: { value: '', answered: false },
  [CONSTANTS.REDUX.INITIAL_AGE]: { value: '', answered: false },
};

const ensureStructured = (raw) => {
  const structured = { ...defaultAnswers };
  if (!raw) return structured;

  Object.keys(structured).forEach((key) => {
    const entry = raw[key];

    if (entry === undefined || entry === null || typeof entry !== 'object') {
      structured[key] = {
        value: entry ?? '',
        answered: entry !== '' && entry !== undefined && entry !== null,
      };
      return;
    }

    const value = 'value' in entry ? entry.value : '';
    const answered =
      'answered' in entry
        ? !!entry.answered
        : value !== '' && value !== undefined && value !== null;

    structured[key] = { value, answered };
  });

  const mapLegacyRisk = (val) => {
    if (val === '1' || val === 1)
      return CONSTANTS.QUESTIONNAIRE.INTERMEDIATE_RISK;
    if (val === '2' || val === 2) return CONSTANTS.QUESTIONNAIRE.HIGH_RISK;
    return val;
  };

  const mapLegacyTreatment = (val) => {
    if (val === '1' || val === 1) return CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR;
    if (val === '2' || val === 2)
      return CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR_AND_SGLT2_INHIBITOR;
    if (val === '3' || val === 3)
      return CONSTANTS.QUESTIONNAIRE
        .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_NSMRA;
    if (val === '4' || val === 4)
      return CONSTANTS.QUESTIONNAIRE
        .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_GLP1RA;
    if (val === '5' || val === 5)
      return CONSTANTS.QUESTIONNAIRE.FOUR_DRUG_THERAPY;
    return val;
  };

  structured[CONSTANTS.REDUX.RISK].value = mapLegacyRisk(
    structured[CONSTANTS.REDUX.RISK].value
  );
  structured[CONSTANTS.REDUX.TREATMENT_PLAN].value = mapLegacyTreatment(
    structured[CONSTANTS.REDUX.TREATMENT_PLAN].value
  );

  return structured;
};

const AnswersContext = createContext();

export const AnswersProvider = ({ children }) => {
  const [answers, setAnswers] = useState(() => {
    try {
      const stored = localStorage.getItem('answers');
      const parsed = stored ? JSON.parse(stored) : null;
      return ensureStructured(parsed);
    } catch (error) {
      console.error('Error loading answers from localStorage:', error);
      return defaultAnswers;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('answers', JSON.stringify(answers));
    } catch (error) {
      console.error('Error saving answers to localStorage:', error);
    }
  }, [answers]);

  const updateAnswers = useCallback((newAnswers) => {
    setAnswers((prev) => {
      const merged = { ...prev };
      Object.entries(newAnswers).forEach(([key, entry]) => {
        merged[key] = { ...prev[key], ...entry };
      });
      return merged;
    });
  }, []);

  return (
    <AnswersContext.Provider value={{ answers, updateAnswers }}>
      {children}
    </AnswersContext.Provider>
  );
};

AnswersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AnswersContext;
